/**
 * The device's own storage, behind one narrow interface.
 *
 * IndexedDB is the only browser store large enough for a workspace's read cache and durable
 * enough for a queue of unsent writes, but it is also the one API that does not exist in the
 * test environment. So everything above this file talks to `OfflineStore`, and the tests run
 * the same logic against an in-memory implementation.
 */

export const storeNames = ['vault', 'cache', 'outbox', 'meta'] as const;

export type StoreName = (typeof storeNames)[number];

export interface StoreEntry<T> {
  readonly key: string;
  readonly value: T;
}

export interface OfflineStore {
  get<T>(store: StoreName, key: string): Promise<T | undefined>;
  put(store: StoreName, key: string, value: unknown): Promise<void>;
  remove(store: StoreName, key: string): Promise<void>;
  /** Every entry in a store, ordered by key — which is why keys are built to sort usefully. */
  entries<T>(store: StoreName): Promise<readonly StoreEntry<T>[]>;
  clear(store: StoreName): Promise<void>;
  clearAll(): Promise<void>;
}

const DATABASE_NAME = 'varza-offline';
const DATABASE_VERSION = 1;

function promisify<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('The offline store rejected a request.'));
  });
}

class IndexedDbStore implements OfflineStore {
  #database: Promise<IDBDatabase> | undefined;

  #open(): Promise<IDBDatabase> {
    this.#database ??= new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
      request.onupgradeneeded = () => {
        for (const name of storeNames) {
          if (!request.result.objectStoreNames.contains(name)) request.result.createObjectStore(name);
        }
      };
      request.onsuccess = () => {
        // A second tab upgrading the schema would otherwise leave this connection blocking it.
        request.result.onversionchange = () => request.result.close();
        resolve(request.result);
      };
      request.onerror = () => reject(request.error ?? new Error('Varza could not open its offline store.'));
      request.onblocked = () => reject(new Error('Another Varza window is holding the offline store open.'));
    });
    return this.#database;
  }

  async #transaction<T>(store: StoreName, mode: IDBTransactionMode, run: (objectStore: IDBObjectStore) => Promise<T>): Promise<T> {
    const database = await this.#open();
    const transaction = database.transaction(store, mode);
    const result = await run(transaction.objectStore(store));
    await new Promise<void>((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onabort = () => reject(transaction.error ?? new Error('The offline store aborted a transaction.'));
      transaction.onerror = () => reject(transaction.error ?? new Error('The offline store failed a transaction.'));
    });
    return result;
  }

  get<T>(store: StoreName, key: string): Promise<T | undefined> {
    return this.#transaction(store, 'readonly', (objectStore) => promisify<T | undefined>(objectStore.get(key)));
  }

  put(store: StoreName, key: string, value: unknown): Promise<void> {
    return this.#transaction(store, 'readwrite', async (objectStore) => {
      await promisify(objectStore.put(value, key));
    });
  }

  remove(store: StoreName, key: string): Promise<void> {
    return this.#transaction(store, 'readwrite', async (objectStore) => {
      await promisify(objectStore.delete(key));
    });
  }

  entries<T>(store: StoreName): Promise<readonly StoreEntry<T>[]> {
    return this.#transaction(store, 'readonly', async (objectStore) => {
      const [keys, values] = await Promise.all([
        promisify(objectStore.getAllKeys()),
        promisify(objectStore.getAll() as IDBRequest<T[]>),
      ]);
      // `getAllKeys` and `getAll` are both in key order, so the two lists line up.
      return keys.map((key, index) => ({ key: String(key), value: values[index] as T }));
    });
  }

  clear(store: StoreName): Promise<void> {
    return this.#transaction(store, 'readwrite', async (objectStore) => {
      await promisify(objectStore.clear());
    });
  }

  async clearAll(): Promise<void> {
    for (const name of storeNames) await this.clear(name);
  }
}

/** The store used when IndexedDB is absent: tests, and any browser that has disabled it. */
export class MemoryOfflineStore implements OfflineStore {
  readonly #data = new Map<StoreName, Map<string, unknown>>();

  #bucket(store: StoreName): Map<string, unknown> {
    let bucket = this.#data.get(store);
    if (!bucket) {
      bucket = new Map();
      this.#data.set(store, bucket);
    }
    return bucket;
  }

  get<T>(store: StoreName, key: string): Promise<T | undefined> {
    return Promise.resolve(this.#bucket(store).get(key) as T | undefined);
  }

  put(store: StoreName, key: string, value: unknown): Promise<void> {
    this.#bucket(store).set(key, value);
    return Promise.resolve();
  }

  remove(store: StoreName, key: string): Promise<void> {
    this.#bucket(store).delete(key);
    return Promise.resolve();
  }

  entries<T>(store: StoreName): Promise<readonly StoreEntry<T>[]> {
    const sorted = [...this.#bucket(store).entries()]
      .sort(([left], [right]) => (left < right ? -1 : left > right ? 1 : 0))
      .map(([key, value]) => ({ key, value: value as T }));
    return Promise.resolve(sorted);
  }

  clear(store: StoreName): Promise<void> {
    this.#bucket(store).clear();
    return Promise.resolve();
  }

  clearAll(): Promise<void> {
    this.#data.clear();
    return Promise.resolve();
  }
}

let store: OfflineStore | undefined;

export function offlineStoreAvailable(): boolean {
  return typeof indexedDB !== 'undefined';
}

/** The one store the application uses. */
export function offlineStore(): OfflineStore {
  store ??= offlineStoreAvailable() ? new IndexedDbStore() : new MemoryOfflineStore();
  return store;
}

/** Replaces the store, for tests. */
export function setOfflineStore(replacement: OfflineStore | undefined): void {
  store = replacement;
}
